import React, { createContext, useContext, useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import Header from "@/components/Header";
import LeftSidebar from "@/components/LeftSidebar";
import RightSidebar from "@/components/RightSidebar";
import ChatDrawer from "@/components/ChatDrawer";
import VerifiedModal from "@/components/VerifiedModal";
import ManageBadgeModal from "@/components/ManageBadgeModal";
import ContactsModal from "@/components/ContactsModal";
import VideoCallModal from "@/components/VideoCallModal";
import IncomingCallModal from "@/components/IncomingCallModal";
import ReportDialog from "@/components/ReportDialog";
import GuidelinesGate from "@/components/GuidelinesGate";
import ConfirmDialog from "@/components/ConfirmDialog";
import { WSProvider, useWS } from "@/context/WSContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const UICtx = createContext(null);
export const useUI = () => useContext(UICtx);

function GlobalWS({ onIncomingCall }) {
  const { subscribe } = useWS();
  const { logout } = useAuth();
  const qc = useQueryClient();
  useEffect(() => {
    return subscribe((data) => {
      if (data.event === "banned") {
        toast.error("Your account has been suspended for violating the Community Guidelines.");
        logout();
      } else if (data.event === "new_report") {
        qc.invalidateQueries({ queryKey: ["admin-reports"] });
        qc.invalidateQueries({ queryKey: ["admin-stats"] });
      } else if (data.event === "notification") {
        qc.invalidateQueries({ queryKey: ["notifications"] });
      } else if (data.event === "message") {
        qc.invalidateQueries({ queryKey: ["conversations"] });
        qc.invalidateQueries({ queryKey: ["messages", data.message?.from_id] });
      } else if (data.event === "call_invite") {
        onIncomingCall(data);
      } else if (data.event === "call_declined") {
        toast.message(`${data.by?.name} ${data.reason === "timeout" ? "didn't answer" : "declined the call"}`);
      }
    });
  }, [subscribe, qc, onIncomingCall, logout]);
  return null;
}

export default function Layout() {
  const [chatUser, setChatUser] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const confirmAction = (opts) => new Promise((resolve) => setConfirmState({ ...opts, resolve }));
  const wide = useLocation().pathname.startsWith("/admin");

  const startCall = async (kind, target_id) => {
    try {
      toast.loading("Starting video call...", { id: "call" });
      const { data } = await api.post("/calls", { kind, target_id });
      toast.dismiss("call");
      setActiveCall({ roomUrl: data.room_url, token: data.token });
    } catch (e) {
      toast.dismiss("call");
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start call");
    }
  };

  const acceptCall = async () => {
    const call = incomingCall;
    setIncomingCall(null);
    try {
      const { data } = await api.post(`/calls/${call.call_id}/join`);
      setActiveCall({ roomUrl: data.room_url, token: data.token });
    } catch (e) {
      toast.error("Could not join call");
    }
  };

  const declineCall = async (reason = "declined") => {
    const call = incomingCall;
    setIncomingCall(null);
    if (call?.call_id) {
      try { await api.post(`/calls/${call.call_id}/decline?reason=${reason}`); } catch {}
    }
  };

  return (
    <WSProvider>
      <UICtx.Provider value={{
        openChat: setChatUser,
        openVerify: () => setVerifyOpen(true),
        openManage: () => setManageOpen(true),
        openContacts: () => setContactsOpen(true),
        startCall,
        openReport: setReportTarget,
        confirmAction,
      }}>
        <GlobalWS onIncomingCall={setIncomingCall} />
        <div className="min-h-screen bg-background">
          <Header />
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {wide ? <Outlet /> : (
            <div className="grid grid-cols-12 gap-6 items-start">
              <aside className="col-span-12 lg:col-span-3 sticky top-20 hidden lg:block space-y-4">
                <LeftSidebar />
              </aside>
              <div className="col-span-12 md:col-span-8 lg:col-span-6 space-y-6">
                <Outlet />
              </div>
              <aside className="col-span-12 md:col-span-4 lg:col-span-3 sticky top-20 hidden md:block space-y-4">
                <RightSidebar />
              </aside>
            </div>
            )}
          </main>
        </div>
        <ChatDrawer chatUser={chatUser} onClose={() => setChatUser(null)} />
        <VerifiedModal open={verifyOpen} onClose={() => setVerifyOpen(false)} />
        <ManageBadgeModal open={manageOpen} onClose={() => setManageOpen(false)} />
        <ContactsModal open={contactsOpen} onClose={() => setContactsOpen(false)} />
        <IncomingCallModal call={incomingCall} onAccept={acceptCall} onDecline={() => declineCall("declined")} onTimeout={() => declineCall("timeout")} />
        <VideoCallModal call={activeCall} onClose={() => setActiveCall(null)} />
        <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
        <GuidelinesGate />
        <ConfirmDialog state={confirmState} onDone={() => setConfirmState(null)} />
      </UICtx.Provider>
    </WSProvider>
  );
}
